import { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls, instrumentationSources, instrumentationPatterns, pagesUnderTest } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import { PROJECT_MAP } from "../utils/thorProjects";
import { serverErrMsg } from "../utils/validators";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import Button from "../../common/elements/Button";
import ConfirmDialog from "./ConfirmDialog";

// ── Constants ──────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
    { value: 'a/b', label: 'A/B Test' },
    { value: 'multivariate', label: 'Multivariate' },
    { value: 'multiarmed_bandit', label: 'Multiarmed Bandit' },
    { value: 'a/a', label: 'A/A Test' },
];

const PROJECT_OPTIONS = Object.entries(PROJECT_MAP)
    .map(([id, name]) => ({ value: Number(id), label: name }))
    .sort((a, b) => {
        if (a.label.startsWith('DO NOT USE')) return 1;
        if (b.label.startsWith('DO NOT USE')) return -1;
        return a.label.localeCompare(b.label);
    });

const SECTION_TITLES = {
    overview: 'Overview',
    hypothesis: 'Hypothesis',
    config: 'Configuration',
    instrumentation: 'Instrumentation',
    stakeholders: 'Stakeholders',
};

// ── Helpers ────────────────────────────────────────────────────────────────

const toMultiOptions = (arr) => (arr || []).map(v => ({ value: v, label: v }));
const toMultiOptionsLookup = (arr, opts) =>
    (arr || []).map(v => opts.find(o => o.value === v) ?? { value: v, label: v });
const fromMultiOptions = (opts) => (opts || []).map(o => o.value);
const toDateInput = (dt) => dt ? new Date(dt).toISOString().slice(0, 10) : '';

const Field = ({ label, help, required, children }) => (
    <div className="field">
        <label className="label is-small">
            {label}{required && <span className="has-text-danger ml-1">*</span>}
        </label>
        <div className="control">{children}</div>
        {help && <p className="help">{help}</p>}
    </div>
);

const BoolField = ({ label, value, onChange, disabled }) => (
    <div className="field">
        <label className="label is-small">{label}</label>
        {disabled ? (
            <p className="is-size-7 has-text-weight-semibold" style={{minHeight: '2em', paddingTop: '0.3em'}}>
                {value === true ? 'Yes' : value === false ? 'No' : <span className="has-text-grey">—</span>}
            </p>
        ) : (
            <div className="buttons has-addons">
                <button type="button"
                    className={`button is-small ${value === true ? 'is-success' : 'is-light has-text-grey'}`}
                    aria-pressed={value === true}
                    onClick={() => onChange(true)}>Yes</button>
                <button type="button"
                    className={`button is-small ${value === false ? 'is-dark' : 'is-light has-text-grey'}`}
                    aria-pressed={value === false}
                    onClick={() => onChange(false)}>No</button>
            </div>
        )}
    </div>
);

// ── Main component ─────────────────────────────────────────────────────────

const DetailsTab = ({ experimentKey, section = 'overview', onArchive, isArchiving = false, isArchived = false, onDirtyChange, onSave, onMetaChange }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [detailsState, setDetailsState] = useState(buildLoadingState(null));
    const [exclusionGroupsState, setExclusionGroupsState] = useState(buildLoadingState([]));
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [form, setForm] = useState({});
    const [readOnlyMeta, setReadOnlyMeta] = useState({ createdAt: null, updatedAt: null });
    const [stakeholders, setStakeholders] = useState({ ANALYST: '', PRODUCT: '', ENGINEER: '' });
    const [archiveConfirm, setArchiveConfirm] = useState(false);

    useEffect(() => {
        return () => { onDirtyChange?.(false); };
    }, [onDirtyChange]);

    const markDirty = () => {
        if (!isDirty) {
            setIsDirty(true);
            onDirtyChange?.(true);
        }
    };

    const set = (key, val) => {
        setForm(f => ({ ...f, [key]: val }));
        markDirty();
    };

    const fetchDetails = useCallback(() => {
        setDetailsState(buildLoadingState(null));
        Promise.all([
            execute('GET', thor_api_urls.experiment(experimentKey)),
            execute('GET', thor_api_urls.experimentStakeholders(experimentKey)),
            execute('GET', thor_api_urls.instrumentationInstructions(experimentKey)).catch(() => null),
        ])
            .then(([rawDetail, sData, instrData]) => {
                // execute() returns undefined when MSAL token isn't ready yet;
                // stay in loading state — fetchDetails will re-run once the token arrives.
                if (rawDetail === undefined && sData === undefined) return;

                const detail = (rawDetail?.experiment && typeof rawDetail.experiment === 'object')
                    ? rawDetail.experiment
                    : (rawDetail && typeof rawDetail === 'object' && !Array.isArray(rawDetail))
                        ? rawDetail
                        : null;
                if (!detail) {
                    setDetailsState(buildErrorState(new Error('Could not load experiment details'), null));
                    return;
                }
                setDetailsState(buildDataState(detail));
                execute('GET', thor_api_urls.experimentExclusionGroups(experimentKey))
                    .then(egData => {
                        const groups = Array.isArray(egData) ? egData
                            : Array.isArray(egData?.experimentExclusionGroups) ? egData.experimentExclusionGroups
                            : Array.isArray(egData?.exclusionGroups) ? egData.exclusionGroups : [];
                        setExclusionGroupsState(buildDataState(groups));
                        const actual = groups.length > 0 ? groups[0].actualTrafficAllocation : detail.trafficAllocation;
                        onMetaChange?.({ actualTrafficAllocation: actual });
                    })
                    .catch(() => setExclusionGroupsState(buildDataState([])));
                setReadOnlyMeta({ createdAt: detail.createdAt ?? null, updatedAt: detail.updatedAt ?? null });
                setForm({
                    name: detail.name ?? '',
                    description: detail.description ?? '',
                    type: detail.type ?? '',
                    projectId: detail.projectId ?? null,
                    email: detail.email ?? '',
                    jiraTicket: detail.jiraTicket ?? detail.ticket ?? '',
                    specUrl: detail.specUrl ?? detail.specification ?? '',
                    productManager: detail.productManager ?? detail.owner ?? '',
                    isMemberTest: detail.memberTest ?? detail.isMemberTest ?? false,
                    isLongRunning: detail.longRunning ?? detail.isLongRunning ?? false,
                    isAnalysisRequired: detail.analysisRequired ?? detail.isAnalysisRequired ?? false,
                    requiresAdhocAnalysis: detail.requiresAdhocAnalysis ?? false,
                    trafficAllocation: detail.trafficAllocation ?? 0,
                    userReshuffleType: detail.userReshuffleType ?? null,
                    daysToPower: detail.daysToPower ?? '',
                    targetStartDate: toDateInput(detail.targetStartDate),
                    hypothesisIf: detail.hypothesisIf ?? '',
                    hypothesisThen: detail.hypothesisThen ?? '',
                    hypothesisBecause: detail.hypothesisBecause ?? '',
                    longTermStrategicGoal: detail.longTermStrategicGoal ?? '',
                    backgroundResearch: detail.backgroundResearch ?? '',
                    audienceDescription: detail.audienceDescription ?? '',
                    instrumentationPattern: instrData?.instrumentationPattern ?? '',
                    instrumentationSources: toMultiOptionsLookup(instrData?.instrumentationSources, instrumentationSources),
                    pagesUnderTest: toMultiOptionsLookup(instrData?.pagesUnderTest, pagesUnderTest),
                    impactedServices: toMultiOptionsLookup(instrData?.impactedServices, instrumentationSources),
                    customEligibilityChecks: instrData?.customEligibilityChecks ?? '',
                    expectedTrafficExposure: instrData?.expectedTrafficExposure ?? '',
                });
                const sMap = sData?.stakeholders ?? {};
                setStakeholders({
                    ANALYST: (sMap.ANALYST || []).join(', '),
                    PRODUCT: (sMap.PRODUCT || []).join(', '),
                    ENGINEER: (sMap.ENGINEER || []).join(', '),
                });
                setIsDirty(false);
                onDirtyChange?.(false);
            })
            .catch(error => {
                console.error("Failed to fetch experiment details:", error);
                setDetailsState(buildErrorState(error, null));
            });
    }, [execute, experimentKey, onDirtyChange]);

    useEffect(() => { fetchDetails(); }, [fetchDetails]);

    const handleSave = () => {
        setIsSaving(true);

        // Instrumentation fields are stored in a separate DB table — use dedicated endpoint
        if (section === 'instrumentation') {
            const instrPayload = {
                instrumentationPattern: form.instrumentationPattern || null,
                customEligibilityChecks: form.customEligibilityChecks || null,
                expectedTrafficExposure: form.expectedTrafficExposure || null,
                instrumentationSources: fromMultiOptions(form.instrumentationSources),
                pagesUnderTest: fromMultiOptions(form.pagesUnderTest),
                impactedServices: fromMultiOptions(form.impactedServices),
            };
            execute('POST', thor_api_urls.instrumentationInstructions(experimentKey), instrPayload)
                .then(() => {
                    addToast('Instrumentation saved.', 'success');
                    setIsDirty(false);
                    onDirtyChange?.(false);
                })
                .catch(error => addToast(serverErrMsg(error, 'Failed to save instrumentation.'), 'danger'))
                .finally(() => setIsSaving(false));
            return;
        }

        const missing = ['name', 'productManager', 'jiraTicket', 'specUrl'].filter(k => !form[k]?.trim());
        if (missing.length) {
            setIsSaving(false);
            addToast(`Required fields missing: ${missing.join(', ')}`, 'danger');
            return;
        }
        // Exclude instrumentation fields — those are managed by the dedicated endpoint
        const { instrumentationPattern, instrumentationSources, pagesUnderTest,
                impactedServices, customEligibilityChecks, expectedTrafficExposure,
                ...rest } = form;
        const trimStr = v => (typeof v === 'string' ? v.trim() : v);
        const payload = {
            ...rest,
            name: trimStr(rest.name),
            description: trimStr(rest.description),
            email: trimStr(rest.email),
            productManager: trimStr(rest.productManager),
            jiraTicket: trimStr(rest.jiraTicket),
            specUrl: trimStr(rest.specUrl),
            hypothesisIf: trimStr(rest.hypothesisIf),
            hypothesisThen: trimStr(rest.hypothesisThen),
            hypothesisBecause: trimStr(rest.hypothesisBecause),
            longTermStrategicGoal: trimStr(rest.longTermStrategicGoal),
            backgroundResearch: trimStr(rest.backgroundResearch),
            targetStartDate: rest.targetStartDate || null,
            daysToPower: rest.daysToPower !== '' ? Number(rest.daysToPower) : null,
            trafficAllocation: Number(rest.trafficAllocation),
        };
        const stakeholderPayload = {
            stakeholders: {
                ANALYST: stakeholders.ANALYST ? stakeholders.ANALYST.split(',').map(s => s.trim()).filter(Boolean) : [],
                PRODUCT: stakeholders.PRODUCT ? stakeholders.PRODUCT.split(',').map(s => s.trim()).filter(Boolean) : [],
                ENGINEER: stakeholders.ENGINEER ? stakeholders.ENGINEER.split(',').map(s => s.trim()).filter(Boolean) : [],
            }
        };
        Promise.all([
            execute('PUT', thor_api_urls.experiment(experimentKey), payload),
            execute('POST', thor_api_urls.experimentStakeholders(experimentKey), stakeholderPayload),
        ])
            .then(() => {
                addToast('Details saved.', 'success');
                setIsDirty(false);
                onDirtyChange?.(false);
                const egList = Array.isArray(exclusionGroupsState.data) ? exclusionGroupsState.data : [];
                const actualAlloc = egList.length > 0 ? egList[0].actualTrafficAllocation : Number(form.trafficAllocation);
                onSave?.({ name: form.name, projectId: form.projectId, trafficAllocation: Number(form.trafficAllocation), actualTrafficAllocation: actualAlloc });
                fetchDetails();
            })
            .catch(error => {
                console.error("Failed to save details:", error);
                addToast(serverErrMsg(error, 'Failed to save details.'), 'danger');
            })
            .finally(() => setIsSaving(false));
    };

    const sp = (disabled) => ({
        isDisabled: disabled,
        ...(disabled ? { components: { MultiValueRemove: () => null } } : {}),
        menuPortalTarget: document.body,
        menuPosition: 'fixed',
        styles: { menuPortal: base => ({ ...base, zIndex: 9999 }) },
    });

    const eg = Array.isArray(exclusionGroupsState.data) ? exclusionGroupsState.data : [];

    const hasInstrumentationData =
        !!form.instrumentationPattern ||
        (form.instrumentationSources?.length > 0) ||
        (form.pagesUnderTest?.length > 0) ||
        !!form.expectedTrafficExposure ||
        !!form.customEligibilityChecks ||
        (form.impactedServices?.length > 0);

    const renderFields = () => {
        switch (section) {
            case 'overview':
                return (
                    <>
                        <div className="columns is-multiline">
                            <div className="column is-half">
                                <Field label={<>Experiment Name<HelpTooltip text={HELP.overview.name} /></>} required>
                                    <input className="input is-small" type="text" value={form.name ?? ''}
                                           disabled={!editMode} onChange={e => set('name', e.target.value)} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Project<HelpTooltip text={HELP.overview.project} /></>}>
                                    <Select options={PROJECT_OPTIONS}
                                        value={PROJECT_OPTIONS.find(o => o.value === form.projectId) ?? null}
                                        onChange={o => set('projectId', o?.value ?? null)}
                                        {...sp(!editMode)} placeholder="Select project…" />
                                </Field>
                            </div>
                            <div className="column is-full">
                                <Field label={<>Description<HelpTooltip text={HELP.overview.description} /></>}>
                                    <textarea className="textarea is-small" rows={2} value={form.description ?? ''}
                                              disabled={!editMode} onChange={e => set('description', e.target.value)} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Created Date<HelpTooltip text={HELP.overview.createdDate} /></>}>
                                    <input className="input is-small" type="text" disabled
                                           value={readOnlyMeta.createdAt ? new Date(readOnlyMeta.createdAt).toLocaleString() : '—'} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Modified Date<HelpTooltip text={HELP.overview.modifiedDate} /></>}>
                                    <input className="input is-small" type="text" disabled
                                           value={readOnlyMeta.updatedAt ? new Date(readOnlyMeta.updatedAt).toLocaleString() : '—'} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Experiment Type<HelpTooltip text={HELP.experimentType} position="bottom" /></>}>
                                    <Select options={TYPE_OPTIONS}
                                        value={TYPE_OPTIONS.find(o => o.value === form.type) ?? null}
                                        onChange={o => set('type', o?.value ?? '')}
                                        {...sp(!editMode)} placeholder="Select type…" />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Traffic Allocation (%)<HelpTooltip text={HELP.config.trafficAllocation} /></>}>
                                    <input className="input is-small" type="number" min={0} max={100}
                                           value={form.trafficAllocation ?? 0} disabled={!editMode}
                                           onChange={e => set('trafficAllocation', e.target.value)} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Actual Traffic Allocation<HelpTooltip text={HELP.overview.actualTraffic} /></>}>
                                    <input className="input is-small" type="text" disabled
                                           value={eg.length > 0 ? `${eg[0].actualTrafficAllocation}%` : `${form.trafficAllocation ?? 0}%`} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Exclusion Group<HelpTooltip text={HELP.overview.exclusionGroup} /></>}>
                                    <input className="input is-small" type="text" disabled
                                           value={eg.length > 0 ? eg[0].exclusionGroupName : '—'} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Owner Email<HelpTooltip text={HELP.overview.ownerEmail} /></>}>
                                    <input className="input is-small" type="email" value={form.email ?? ''}
                                           disabled={!editMode} onChange={e => set('email', e.target.value)} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Product Manager<HelpTooltip text={HELP.overview.productManager} /></>} required>
                                    <input className="input is-small" type="text" value={form.productManager ?? ''}
                                           disabled={!editMode} onChange={e => set('productManager', e.target.value)} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Jira Ticket<HelpTooltip text={HELP.overview.jiraTicket} /></>} required>
                                    <input className="input is-small" type="text" value={form.jiraTicket ?? ''}
                                           disabled={!editMode} onChange={e => set('jiraTicket', e.target.value)} />
                                </Field>
                            </div>
                            <div className="column is-half">
                                <Field label={<>Spec URL<HelpTooltip text={HELP.overview.specUrl} /></>} required>
                                    {!editMode && form.specUrl ? (
                                        <div style={{ padding: '0.3rem 0' }}>
                                            <a href={form.specUrl} target="_blank" rel="noopener noreferrer"
                                               className="is-size-7" style={{ wordBreak: 'break-all' }}>
                                                {form.specUrl}
                                            </a>
                                        </div>
                                    ) : (
                                        <input className="input is-small" type="text" value={form.specUrl ?? ''}
                                               disabled={!editMode} onChange={e => set('specUrl', e.target.value)} />
                                    )}
                                </Field>
                            </div>
                        </div>

                        {!isArchived && onArchive && (
                            <div style={{ marginTop: '2.5rem', borderTop: '1px solid #dbdbdb', paddingTop: '1.25rem' }}>
                                <p className="is-size-7 has-text-grey mb-3">
                                    Archiving hides this experiment from active lists. This action cannot be undone from this UI.
                                </p>
                                <Button
                                    className="is-danger is-light is-small"
                                    disabled={!editMode || isArchiving}
                                    title={!editMode ? 'Enable Edit mode to archive' : 'Archive this experiment'}
                                    onClick={() => setArchiveConfirm(true)}>
                                    {isArchiving ? 'Archiving…' : 'Archive Experiment'}
                                </Button>
                            </div>
                        )}
                    </>
                );

            case 'hypothesis':
                return (
                    <div className="columns is-multiline">
                        {!editMode && !form.hypothesisIf && !form.hypothesisThen && !form.hypothesisBecause && (
                            <div className="column is-full">
                                <div className="notification is-light has-text-grey is-size-7 mb-4">
                                    No hypothesis has been defined yet. Enable Edit mode to add one.
                                </div>
                            </div>
                        )}
                        <div className="column is-full">
                            <Field label={<>If…<HelpTooltip text={HELP.hypothesis.if} /></>}>
                                <textarea className="textarea is-small" rows={3} value={form.hypothesisIf ?? ''}
                                          disabled={!editMode} onChange={e => set('hypothesisIf', e.target.value)} />
                            </Field>
                        </div>
                        <div className="column is-full">
                            <Field label={<>Then…<HelpTooltip text={HELP.hypothesis.then} /></>}>
                                <textarea className="textarea is-small" rows={3} value={form.hypothesisThen ?? ''}
                                          disabled={!editMode} onChange={e => set('hypothesisThen', e.target.value)} />
                            </Field>
                        </div>
                        <div className="column is-full">
                            <Field label={<>Because…<HelpTooltip text={HELP.hypothesis.because} /></>}>
                                <textarea className="textarea is-small" rows={3} value={form.hypothesisBecause ?? ''}
                                          disabled={!editMode} onChange={e => set('hypothesisBecause', e.target.value)} />
                            </Field>
                        </div>
                        <div className="column is-full">
                            <Field label={<>Long-term Strategic Goal<HelpTooltip text={HELP.hypothesis.strategicGoal} /></>}>
                                <textarea className="textarea is-small" rows={2} value={form.longTermStrategicGoal ?? ''}
                                          disabled={!editMode} onChange={e => set('longTermStrategicGoal', e.target.value)} />
                            </Field>
                        </div>
                        <div className="column is-full">
                            <Field label={<>Background Research<HelpTooltip text={HELP.hypothesis.backgroundResearch} /></>}>
                                <textarea className="textarea is-small" rows={2} value={form.backgroundResearch ?? ''}
                                          disabled={!editMode} onChange={e => set('backgroundResearch', e.target.value)} />
                            </Field>
                        </div>
                    </div>
                );

            case 'config':
                return (
                    <div className="columns is-multiline">
                        {!editMode && (
                            <div className="column is-full">
                                <p className="help has-text-grey mb-3">Experiment configuration settings. Enable Edit mode to modify.</p>
                            </div>
                        )}
                        <div className="column is-half">
                            <Field label={<>Days to Power<HelpTooltip text={HELP.config.daysToPower} /></>}>
                                <input className="input is-small" type="number" min={0}
                                       value={form.daysToPower ?? ''} disabled={!editMode}
                                       onChange={e => set('daysToPower', e.target.value)} />
                            </Field>
                        </div>
                        <div className="column is-half">
                            <Field label={<>Target Start Date<HelpTooltip text={HELP.config.targetStartDate} /></>}>
                                <input className="input is-small" type="date" value={form.targetStartDate ?? ''}
                                       disabled={!editMode} onChange={e => set('targetStartDate', e.target.value)} />
                            </Field>
                        </div>
                        <div className="column is-one-quarter">
                            <BoolField label={<>Long-running<HelpTooltip text={HELP.config.longRunning} position="right" /></>}
                                       value={form.isLongRunning} onChange={v => set('isLongRunning', v)} disabled={!editMode} />
                        </div>
                        <div className="column is-one-quarter">
                            <BoolField label={<>Member Test<HelpTooltip text={HELP.config.memberTest} position="right" /></>}
                                       value={form.isMemberTest} onChange={v => set('isMemberTest', v)} disabled={!editMode} />
                        </div>
                        <div className="column is-one-quarter">
                            <BoolField label={<>Analysis Required<HelpTooltip text={HELP.config.analysisRequired} position="right" /></>}
                                       value={form.isAnalysisRequired} onChange={v => set('isAnalysisRequired', v)} disabled={!editMode} />
                        </div>
                        <div className="column is-one-quarter">
                            <BoolField label={<>Ad-hoc Analysis<HelpTooltip text={HELP.config.adhocAnalysis} position="right" /></>}
                                       value={form.requiresAdhocAnalysis} onChange={v => set('requiresAdhocAnalysis', v)} disabled={!editMode} />
                        </div>
                        <div className="column is-half">
                            <Field label={<>Salt (Reshuffle Type)<HelpTooltip text={HELP.config.salt} /></>}>
                                <div className="select is-small is-fullwidth">
                                    <select value={form.userReshuffleType ?? ''} disabled={!editMode}
                                            onChange={e => set('userReshuffleType', e.target.value || null)}>
                                        <option value="">None (Default)</option>
                                        <option value="DAY">DAY</option>
                                    </select>
                                </div>
                            </Field>
                        </div>
                    </div>
                );

            case 'instrumentation':
                return (
                    <div className="columns is-multiline">
                        {!editMode && !hasInstrumentationData && (
                            <div className="column is-full">
                                <div className="notification is-info is-light">
                                    <p><strong>Instrumentation not yet configured.</strong></p>
                                    <p>This section is filled in by the assigned engineer. Enable Edit mode (top-right button) to fill in the tracking plan details.</p>
                                </div>
                            </div>
                        )}
                        <div className="column is-half">
                            <Field label={<>Instrumentation Pattern<HelpTooltip text={HELP.instrumentation.pattern} /></>}>
                                <Select options={instrumentationPatterns}
                                    value={instrumentationPatterns.find(o => o.value === form.instrumentationPattern) ?? null}
                                    onChange={o => set('instrumentationPattern', o?.value ?? '')}
                                    {...sp(!editMode)} placeholder="Select pattern…" />
                            </Field>
                        </div>
                        <div className="column is-half">
                            <Field label={<>Expected Traffic Exposure<HelpTooltip text={HELP.instrumentation.expectedExposure} /></>}>
                                <input className="input is-small" type="text" value={form.expectedTrafficExposure ?? ''}
                                       disabled={!editMode} onChange={e => set('expectedTrafficExposure', e.target.value)} />
                            </Field>
                        </div>
                        <div className="column is-full">
                            <Field label={<>Instrumentation Sources<HelpTooltip text={HELP.instrumentation.sources} /></>}>
                                <Select isMulti options={instrumentationSources}
                                    value={form.instrumentationSources ?? []}
                                    onChange={v => set('instrumentationSources', v)}
                                    {...sp(!editMode)} placeholder="Select sources…" />
                            </Field>
                        </div>
                        <div className="column is-full">
                            <Field label={<>Pages Under Test<HelpTooltip text={HELP.instrumentation.pages} /></>}>
                                <Select isMulti options={pagesUnderTest}
                                    value={form.pagesUnderTest ?? []}
                                    onChange={v => set('pagesUnderTest', v)}
                                    {...sp(!editMode)} placeholder="Select pages…" />
                            </Field>
                        </div>
                        <div className="column is-full">
                            <Field label={<>Custom Eligibility Checks<HelpTooltip text={HELP.instrumentation.customEligibility} /></>}>
                                <textarea className="textarea is-small" rows={2} value={form.customEligibilityChecks ?? ''}
                                          disabled={!editMode} onChange={e => set('customEligibilityChecks', e.target.value)} />
                            </Field>
                        </div>
                        <div className="column is-full">
                            <Field label={<>Impacted Services<HelpTooltip text={HELP.instrumentation.impactedServices} /></>}>
                                <Select isMulti options={instrumentationSources}
                                    value={form.impactedServices ?? []}
                                    onChange={v => set('impactedServices', v)}
                                    {...sp(!editMode)} placeholder="Select services…" />
                            </Field>
                        </div>
                    </div>
                );

            case 'stakeholders':
                return (
                    <>
                        <div className="columns is-multiline mb-4">
                            {['ANALYST', 'PRODUCT', 'ENGINEER'].map(type => {
                                const labelMap = { ANALYST: 'Analyst', PRODUCT: 'Product Manager', ENGINEER: 'Engineer' };
                                const helpMap = { ANALYST: HELP.stakeholders.analyst, PRODUCT: HELP.stakeholders.product, ENGINEER: HELP.stakeholders.engineer };
                                return (
                                    <div className="column is-one-third" key={type}>
                                        <Field label={<>{labelMap[type]}<HelpTooltip text={helpMap[type]} /></>}
                                               help="Comma-separated emails">
                                            <input className="input is-small" type="text"
                                                   value={stakeholders[type] ?? ''}
                                                   disabled={!editMode}
                                                   onChange={e => { setStakeholders(s => ({ ...s, [type]: e.target.value })); markDirty(); }} />
                                        </Field>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <StateHandler showLoader={detailsState.loading} showError={detailsState.error}
                      loadingScreen={<SmallLoadingSpinner />}>
            <div className="level mb-4">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">{SECTION_TITLES[section] ?? 'Details'}</p>
                </div>
                <div className="level-right">
                    <Button className="is-link is-small" onClick={handleSave}
                            disabled={!editMode || isSaving}>
                        {isSaving ? 'Saving…' : 'Save Changes'}
                    </Button>
                </div>
            </div>
            {renderFields()}
            <ConfirmDialog
                isActive={archiveConfirm}
                title="Archive Experiment"
                message={`Archive "${form.name ?? experimentKey}"? This will hide it from the active experiments list. It cannot be undone from this UI.`}
                confirmLabel={isArchiving ? 'Archiving…' : 'Archive'}
                confirmClass="is-danger"
                onConfirm={() => { setArchiveConfirm(false); onArchive?.(); }}
                onCancel={() => setArchiveConfirm(false)}
            />
        </StateHandler>
    );
};

export default DetailsTab;
